<template>
  <div>
    <transition-group name="list">
      <template v-for="category in sortedCategories">
        <v-subheader :key="`subheader-${category.id}`" class="pl-2">
          {{ category.title }}
        </v-subheader>
        <v-layout :key="`layout-${category.id}`" row wrap>
          <transition-group name="list" tag="v-layout" class="row wrap">
            <dish-item
              v-for="selection in category.selections"
              :key="`${selection.id}`"
              :selection="selection"
              :disabled="isDisabled(selection)"
              :opaque="isDisabled(selection)"
              :dishes-in-cart="selectionsInCart(selection)"
              :dishes-in-order="selectionsInOrder(selection)"
              :hide-price="hidePrice"
              :can-afford-dish="canAffordDish(selection)"
              @oncontextmenu="(e, b) => onShowContextMenu(e, b)"
              @addSelection="selection => addSelection( { selection, isSecondary: false })"
            />
          </transition-group>
        </v-layout>
      </template>
    </transition-group>
    <v-menu v-model="showContextMenu" :position-x="contextMenuX" :position-y="contextMenuY">
      <v-list dense>
        <v-list-tile @click="addAdditionalSelection">
          <v-list-tile-avatar>
            <v-icon color="green">add</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-title><span v-t="'dishesContainer.addAdditional'" /></v-list-tile-title>
        </v-list-tile>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import orderByFunc from 'lodash/orderBy';
import flatten from 'lodash/flatten';
import { mapGetters } from 'vuex';
import Big from 'big.js';
import DishItem from '../dishes-item/DishItem.vue';

export default {
  name: 'DishCategories',
  components: {
    'dish-item': DishItem,
  },
  props: {
    categories: {
      type: Array,
      default: () => [],
    },
    orderBy: {
      type: String,
      default: undefined,
    },
    disableContextMenu: Boolean,
    disabled: Boolean,
    isFreeOfCharge: Boolean,
    hidePrice: Boolean,
    selectionsInCart: {
      type: Function,
      default: () => {},
    },
    selectionsInOrder: {
      type: Function,
      default: () => {},
    },
  },
  data: () => ({
    contextMenuX: 0,
    contextMenuY: 0,
    showContextMenu: false,
    contextSelection: null,
    sortedCategories: [],
  }),
  computed: {
    ...mapGetters('userInfoStore', ['userData', 'cities']),
    ...mapGetters('dishesStore', ['activeSelections', 'nonConfirmedSelections', 'confirmedSelections']),
  },
  methods: {
    reorderItems(orderBy) {
      this.sortedCategories = this.categories.map((c) => {
        const getSelectionTitle = selection => selection.dishes[0].title.toLowerCase();
        if (orderBy === 'alpha') {
          const selections = [
            ...orderByFunc(c.selections.filter(s => !this.maxOrdersLimitReached(s)), getSelectionTitle),
            ...c.selections.filter(s => this.maxOrdersLimitReached(s)),
          ];
          return {
            title: c.title,
            id: c.id,
            selections: selections.map(s => ({
              ...s,
              categoryId: c.id,
              isFreeOfCharge: this.isFreeOfCharge,
            })),
          };
        }
        if (orderBy === 'price') {
          const selections = [
            ...orderByFunc(c.selections.filter(s => !this.maxOrdersLimitReached(s)), [s => s.price, s => getSelectionTitle(s)]),
            ...orderByFunc(c.selections.filter(s => this.maxOrdersLimitReached(s)), [s => s.price, s => getSelectionTitle(s)]),
          ];
          return {
            title: c.title,
            id: c.id,
            selections: selections.map(s => ({
              ...s,
              categoryId: c.id,
              isFreeOfCharge: this.isFreeOfCharge,
            })),
          };
        }
        if (orderBy === 'pop') {
          const selections = [
            ...orderByFunc(c.selections.filter(s => !this.maxOrdersLimitReached(s)), [s => s.requestsCount, s => getSelectionTitle(s)], ['desc', 'asc']),
            ...orderByFunc(c.selections.filter(s => this.maxOrdersLimitReached(s)), [s => s.requestsCount, s => getSelectionTitle(s)], ['desc', 'asc']),
          ];
          return {
            title: c.title,
            id: c.id,
            selections: selections.map(s => ({
              ...s,
              categoryId: c.id,
              isFreeOfCharge: this.isFreeOfCharge,
            })),
          };
        }

        return undefined;
      });
    },
    maxOrdersLimitReached(selection) {
      return selection.maxCount != null
        && this.nonConfirmedSelections.filter(as => as.id === selection.id).length + selection.requestsCount >= selection.maxCount;
    },
    updateItems() {
      const allSelections = flatten(
        this.categories.map(c => c.selections.map(s => ({
          ...s,
          categoryId: c.id,
          isFreeOfCharge: this.isFreeOfCharge,
        }))),
      );
      this.sortedCategories = this.sortedCategories.map(c => Object.assign({}, c, {
        selections: c.selections.map(s => allSelections.find(se => se.id === s.id)),
      }));
    },
    onShowContextMenu(e, selection) {
      if (this.disableContextMenu || (this.isDisabled(selection) || !this.canAffordDish(selection))) {
        return;
      }
      e.preventDefault();
      this.showContextMenu = false;
      this.contextMenuX = e.clientX;
      this.contextMenuY = e.clientY;
      this.$nextTick(() => {
        this.showContextMenu = true;
        this.contextSelection = selection;
      });
    },
    addSelection(data) {
      this.$emit('addSelection', data);
    },
    addAdditionalSelection() {
      this.$emit('addSelection', { selection: this.contextSelection, isSecondary: true });
    },
    isDisabled(selection) {
      return this.disabled || this.maxOrdersLimitReached(selection);
    },
    canAffordDish(selection) {
      const totalConfirmedPrice = this.confirmedSelections.reduce((acc, val) => acc.plus(val.price), new Big(0));
      const totalActivePrice = this.activeSelections.reduce((acc, val) => acc.plus(val.price), new Big(0));
      const totalBalanceDifference = totalConfirmedPrice.minus(totalActivePrice);
      const userBalance = new Big(this.userData.balance);
      const availableBalance = userBalance.plus(totalBalanceDifference);
      return this.isFreeOfCharge || availableBalance.round(2) >= selection.price;
    },
  },
};
</script>

<style scoped>
.list-leave-active {
  display: none;
}
.list-enter-active,
.list-leave-to,
.list-move,
.list-enter-to {
  transition: cubic-bezier(0.645, 0.045, 0.355, 1) 0.25s;
}
.list-enter,
.list-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
